import * as Dialog from '@radix-ui/react-dialog'
import { keyframes } from '@stitches/react'
import { PropsWithChildren } from 'react'
import { CSS, styled } from '../../lib/stitches'
import { gray11, gray15, gray2, gray3, gray5, gray8 } from '../platform/color'
import { Column, IconButton, Row, Text } from './Components'
import { FieldLabel } from './FieldLabel'
import { CloseIcon } from './icons'

const StyledTrigger = styled(Dialog.Trigger, { all: 'unset' })

export const Modal = (
  props: PropsWithChildren<{
    trigger?: JSX.Element
    onClickClose?: () => void
    open?: boolean
    onOpenChange?: (open: boolean) => void
    contentCss?: CSS
    closeButtonCss?: CSS
    noPadding?: boolean
  }>
) => (
  <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
    {props.trigger && <StyledTrigger asChild>{props.trigger}</StyledTrigger>}
    <Dialog.Portal>
      <Overlay>
        <Content css={props.contentCss} noPadding={props.noPadding}>
          <Dialog.Close onClick={props.onClickClose} asChild={true}>
            <IconButton
              css={{ position: 'absolute', right: '25px', top: '25px', ...props.closeButtonCss }}
            >
              <CloseIcon style={{ color: 'black', width: '20px', height: '20px' }} />
            </IconButton>
          </Dialog.Close>
          {props.children}
        </Content>
      </Overlay>
    </Dialog.Portal>
  </Dialog.Root>
)

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 }
})

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
})

const Overlay = styled(Dialog.Overlay, {
  backgroundColor: 'rgba(0,0,0,.5)',
  position: 'fixed',
  inset: 0,
  animation: `${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  zIndex: 1350
})

const Content = styled(Dialog.Content, {
  position: 'fixed',
  top: '50%',
  left: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  transform: 'translate(-50%, -50%)',
  outline: 'none',
  borderRadius: '16px',
  fontFamily: 'DM Sans',
  color: gray8,
  background: gray2,
  padding: '50px',
  animation: `${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  maxHeight: '95vh',
  overflowY: 'auto',
  width: '95vw',
  '@mobile': {
    width: 'fit-content'
  },
  variants: {
    noPadding: {
      true: {
        padding: '0px'
      }
    }
  }
})

const SectionHeader = styled(FieldLabel, {
  fontSize: '16px',
  paddingBottom: '16px',
  variants: {
    bold: {
      true: {
        fontWeight: '700px'
      }
    }
  }
})

const ColumnLabel = styled(Text, {
  margin: '0',
  color: gray11,
  fontSize: '14px',
  padding: '12px 12px'
})

const HeaderContainer = styled(Column, {
  borderBottom: `1px solid ${gray3}`
})

const FooterContainer = styled(Row, {
  padding: '24px 0px 0px 0px',
  borderTop: `1px solid ${gray3}`,
  variants: {
    stretch: {
      true: {
        justifyContent: 'stretch'
      }
    },
    end: {
      true: {
        justifyContent: 'end'
      }
    },
    column: {
      true: {
        flexDirection: 'column'
      }
    }
  }
})

const StyledFieldLabel = styled('label', {
  fontSize: '12px',
  lineHeight: '20px',
  paddingBottom: '6px',
  fontWeight: 700,
  color: 'black'
})

const TextField = styled('input', {
  border: '1px solid ' + gray5,
  borderRadius: '6px',
  background: 'transparent',
  padding: '10px 12px',
  fontSize: '14px',
  lineHeight: '20px',
  outline: 'none',
  color: gray15,
  transition: 'all 100ms ease',
  marginBottom: '12px',
  '&:placeholder': {
    color: gray8
  },
  '&:focus': {
    boxShadow: '0px 0px 0px 3px ' + gray3
  }
})

const Container = styled(Column, {
  padding: '24px 0px 24px 0px'
})

export const Title = styled('h1', {
  fontSize: '22px',
  lineHeight: '28px',
  fontWeight: 700,
  color: gray15,
  marginBottom: '8px',
  marginRight: '16px'
})

export const Subtitle = styled('h5', {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  marginBottom: '16px'
})

export const ModalComponents = {
  SectionHeader,
  ColumnLabel,
  HeaderContainer,
  FooterContainer,
  Container,
  TextField,
  FieldLabel: StyledFieldLabel,
  Title,
  Subtitle
}
